<template>
  <div
    v-if="(coRegCampaignSubs.length ?? 0) > 0"
    class="w-full px-8 grid gap-y-6 py-8"
  >
    <FormHeader
      :primary-color="primaryColor"
      progress="2"
    >
      <template #title>
        <p>
          {{ __('co_reg_lander_002_step_3_text') }}
        </p>
      </template>

      <template #subtext>
        <p>
          {{ __('co_reg_lander_002_step_3_subtext') }}
        </p>
      </template>
    </FormHeader>

    <div>
      <div
        class="ml-auto flex justify-end w-full border-2 rounded-xl px-4 py-2"
        :style="{
          'backgroundColor': `#${secondaryColor}`,
          'borderColor': `#${primaryColor}`,
        }"
        @click="selectAll()"
      >
        <p class="my-auto mr-2 text-gray-600">
          {{ __('label_select_all_panels') }}
        </p>

        <input
          id="select_all"
          :checked="allAreSelected()"
          type="checkbox"
          name="select_all"
          class="rounded-md h-6 w-6"
          :style="{
            'borderColor': `#${primaryColor}`,
            'backgroundColor': allAreSelected() ? `#${primaryColor}` : '#ffffff',
          }"
        >
      </div>
    </div>

    <div
      v-for="(campaignSub, index) in coRegCampaignSubs"
      :key="index"
      class="shadow-md bg-white border-2 rounded-xl"
      :style="{
        'borderColor': `#${primaryColor}`,
      }"
    >
      <div class="p-4">
        <img
          v-if="campaignSub.co_reg_image_url"
          :src="campaignSub.co_reg_image_url"
          class="h-12 pb-4 object-contain"
        >

        <p class="text-gray-600">
          {{ campaignSub.co_reg_title }}
        </p>

        <!-- eslint-disable vue/no-v-html -->
        <ul
          v-if="parseList(campaignSub.co_reg_description).length > 0"
          class="py-4 list-inside list-disc"
        >
          <li
            v-for="(item, itemIndex) in parseList(campaignSub.co_reg_description)"
            :key="itemIndex"
            v-html="item"
          />
        </ul>
        <div
          v-else
          class="py-4"
          v-html="campaignSub.co_reg_description"
        />
        <!-- eslint-enable vue/no-v-html -->

        <a
          href="#"
          class="text-sm"
          :style="{
            'color': `#${primaryColor}`,
          }"
        >
          <span
            class="cursor-pointer hover:underline"
            @click="openTermsAndConditionsModal(campaignSub.co_reg_conditions, campaignSub.co_reg_conditions_link)"
          >{{ __('co_reg_lander_001_text_conditions') }}</span> • <span
            class="cursor-pointer hover:underline"
            @click="openTermsAndConditionsModal(campaignSub.co_reg_privacy, campaignSub.co_reg_privacy_link)"
          >{{ __('co_reg_lander_001_text_privacy') }}</span>
        </a>

        <div
          class="border-t-2"
          :style="{
            'borderColor': `#${primaryColor}`,
          }"
        />

        <div class="w-full flex pt-4 justify-end">
          <p class="my-auto mr-2 text-gray-600">
            {{ __('label_select_panel') }}
          </p>

          <input
            :id="`select_${campaignSub.id}`"
            :checked="isSelected(campaignSub.id)"
            type="checkbox"
            name="select_all"
            class="rounded-md h-6 w-6"
            :style="{
              'borderColor': `#${primaryColor}`,
              'backgroundColor': isSelected(campaignSub.id) ? `#${primaryColor}` : '#ffffff',
            }"
            @click="selectCampaignSub(campaignSub.id)"
          >
        </div>
      </div>
    </div>

    <div>
      <p
        v-if="shouldShowError"
        class="pb-2 text-red-500"
      >
        {{ __('validation_select_at_least_one') }}
      </p>

      <ErrorMessage name="selected" />

      <FormFooter
        :primary-color="primaryColor"
        :button-color="buttonColor"
        :can-submit="canSubmit"
        step="3"
        @submit="submit()"
      />
    </div>
  </div>
  <div
    v-else-if="loading"
    class="w-full px-8 grid gap-y-6 py-8 grid gap-y-8"
  >
    <div>
      <div class="py-4 rounded shadow-md w-full animate-pulse bg-gray-50">
        <div class="p-4 space-y-4 sm:px-8">
          <div class="w-full h-4 rounded bg-gray-300" />
          <div class="w-full h-4 rounded bg-gray-300" />
          <div class="w-3/4 h-4 rounded bg-gray-300" />
        </div>
      </div>
    </div>

    <div>
      <div class="py-4 rounded shadow-md w-full animate-pulse bg-gray-50">
        <div class="p-4 space-y-4 sm:px-8">
          <div class="w-full h-4 rounded bg-gray-300" />
          <div class="w-1/2 h-4 rounded bg-gray-300" />
          <div class="w-3/4 h-4 rounded bg-gray-300" />
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="w-full px-8 grid gap-y-6 px-8 py-4 grid gap-y-8">
      <p class="text-4xl font-bold pb-2 text-center">
        {{ __('co_reg_lander_002_text_no_campaigns') }}
      </p>

      <p class="text-gray-500 text-center">
        {{ __('co_reg_lander_002_subtext_no_campaigns') }}
      </p>
    </div>
  </div>

  <TermsAndConditionsModal
    class="z-20"
    max-width="90%"
    :text="termsAndConditionsModalText"
    :link="termsAndConditionsModalLink"
    :show="termsAndConditionsModalOpen"
    @close="termsAndConditionsModalOpen = false"
  />
</template>

<script setup>
import {ref} from 'vue';
import ErrorMessage from '@timon/js/Components/ErrorMessage.vue';
import {useStore} from 'vuex';
import FormFooter from '@timon/js/Templates/CO-REG-LANDER-002/Components/FormFooter.vue';
import FormHeader from '@timon/js/Templates/CO-REG-LANDER-002/Components/FormHeader.vue';
import {isPreview} from '@timon/js/Mixins/helpers';
import TermsAndConditionsModal from '@timon/js/Components/TermsAndConditionsModal.vue';

const emit = defineEmits(['submit']);

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },

  coRegCampaignSubs: {
    type: Array,
    required: true,
  },

  loading: {
    type: Boolean,
    required: true,
  },

  canSubmit: {
    type: Boolean,
    required: true,
  },
});

const primaryColor = ref(props.config.style?.primary);
const secondaryColor = ref(props.config.style?.secondary);
const buttonColor = ref(props.config.style?.buttons);

const shouldShowError = ref(false);
const store = useStore();

const termsAndConditionsModalOpen = ref(false);
const termsAndConditionsModalText = ref(null);
const termsAndConditionsModalLink = ref(null);

const openTermsAndConditionsModal = (text, link) => {
  termsAndConditionsModalText.value = text;
  termsAndConditionsModalLink.value = link;
  termsAndConditionsModalOpen.value = true;
};

const selectAll = () => {
  if (allAreSelected()) {
    store.state.form.selected_co_reg_campaign_subs = [];

    return;
  }

  store.state.form.selected_co_reg_campaign_subs = props.coRegCampaignSubs.map((campaignSub) => campaignSub.id);
};

const selectCampaignSub = (campaignSubId) => {
  if (store.state.form.selected_co_reg_campaign_subs.includes(campaignSubId)) {
    store.state.form.selected_co_reg_campaign_subs = store.state.form.selected_co_reg_campaign_subs.filter((id) => id !== campaignSubId);
  } else {
    store.state.form.selected_co_reg_campaign_subs = [...store.state.form.selected_co_reg_campaign_subs, campaignSubId];
  }
};

const isSelected = (campaignSubId) => store.state.form.selected_co_reg_campaign_subs.includes(campaignSubId);

const allAreSelected = () => store.state.form.selected_co_reg_campaign_subs.length === props.coRegCampaignSubs.length;

const submit = () => {
  store.commit('errors/clearErrors');

  if (isPreview()) {
    emit('submit');
    return;
  }

  if (!props.canSubmit) {
    return;
  }

  shouldShowError.value = false;

  if (store.state.form.selected_co_reg_campaign_subs.length === 0) {
    shouldShowError.value = true;
    return;
  }

  emit('submit');
};

function parseList(string) {
  return string.includes('\n') ? string.split('\n') : [];
}
</script>
